/**
 * URL-as-state model for lists (PRD §4.11, T-37): a small schema describes a
 * list's filters, sort and page; these pure functions parse them from the URL,
 * write a change back (dropping defaults so URLs stay short, keeping params the
 * list does not own, resetting the page when a filter changes) and build the
 * canonical query a saved view stores. `hooks/useListParams` binds it to the
 * router; tests: `src/utils/__tests__/listParams.test.ts`.
 */

export type ParamSpec =
  | { kind: 'string'; default?: string; pattern?: RegExp }
  | { kind: 'enum'; values: readonly string[]; default?: string }
  | { kind: 'bool'; default?: boolean }
  | { kind: 'int'; default?: number; min?: number; max?: number };

export type ListSchema = Record<string, ParamSpec>;

type ValueOf<P extends ParamSpec> = P extends { kind: 'bool' } ? boolean : P extends { kind: 'int' } ? number : string;

export type ListValues<S extends ListSchema> = { [K in keyof S]: ValueOf<S[K]> };

/** Value a param has when it is absent from the URL. */
export function defaultOf(spec: ParamSpec): string | number | boolean {
  switch (spec.kind) {
    case 'bool': return spec.default ?? false;
    case 'int': return spec.default ?? 0;
    default: return spec.default ?? '';
  }
}

/** Parse one raw URL value; anything invalid falls back to the default. */
export function parseValue(spec: ParamSpec, raw: string | null): string | number | boolean {
  if (raw === null) return defaultOf(spec);
  switch (spec.kind) {
    case 'bool':
      if (raw === 'true' || raw === '1') return true;
      if (raw === 'false' || raw === '0') return false;
      return defaultOf(spec);
    case 'int': {
      if (!/^-?\d+$/.test(raw.trim())) return defaultOf(spec);
      let n = parseInt(raw, 10);
      if (spec.min !== undefined) n = Math.max(spec.min, n);
      if (spec.max !== undefined) n = Math.min(spec.max, n);
      return n;
    }
    case 'enum':
      return spec.values.includes(raw) ? raw : defaultOf(spec);
    case 'string':
      if (spec.pattern && raw !== '' && !spec.pattern.test(raw)) return defaultOf(spec);
      return raw;
  }
}

/** URL text for a value, or null when the value is the default (param dropped). */
export function serializeValue(spec: ParamSpec, value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (value === defaultOf(spec)) return null;
  switch (spec.kind) {
    case 'bool': return value ? 'true' : 'false';
    case 'int': return Number.isFinite(value as number) ? String(Math.trunc(value as number)) : null;
    case 'enum': return typeof value === 'string' && spec.values.includes(value) ? value : null;
    case 'string': {
      const s = String(value);
      if (s === '') return null;
      if (spec.pattern && !spec.pattern.test(s)) return null;
      return s;
    }
  }
}

export function parseParams<S extends ListSchema>(schema: S, sp: URLSearchParams): ListValues<S> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(schema)) out[key] = parseValue(schema[key], sp.get(key));
  return out as ListValues<S>;
}

export interface PatchOptions {
  /** The page param; it returns to its default when any other key changes. */
  pageKey?: string;
}

/**
 * Write `patch` onto a copy of `sp`. Keys outside the schema are kept (e.g.
 * `from=home`), defaults are dropped, and a change to any filter resets the
 * page unless the patch sets the page itself.
 */
export function applyPatch<S extends ListSchema>(
  schema: S,
  sp: URLSearchParams,
  patch: Partial<ListValues<S>>,
  opts: PatchOptions = {},
): URLSearchParams {
  const next = new URLSearchParams(sp);
  let filterChanged = false;
  for (const key of Object.keys(patch)) {
    const spec = schema[key];
    if (!spec) continue;
    const before = next.get(key);
    const text = serializeValue(spec, (patch as Record<string, unknown>)[key]);
    if (text === null) next.delete(key);
    else next.set(key, text);
    if (key !== opts.pageKey && before !== next.get(key)) filterChanged = true;
  }
  if (opts.pageKey && filterChanged && !(opts.pageKey in patch)) next.delete(opts.pageKey);
  return next;
}

/**
 * Canonical query of the given keys (sorted, defaults dropped). Two states
 * with the same filters give the same string, so a saved view can be matched
 * against the current URL.
 */
export function canonicalQuery<S extends ListSchema>(schema: S, values: Partial<ListValues<S>>, keys: readonly (keyof S & string)[]): string {
  const parts: [string, string][] = [];
  for (const key of keys) {
    const spec = schema[key];
    if (!spec) continue;
    const text = serializeValue(spec, (values as Record<string, unknown>)[key]);
    if (text !== null) parts.push([key, text]);
  }
  parts.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  return new URLSearchParams(parts).toString();
}

/** Normalise a stored query string (sorted keys, schema-valid values only). */
export function normalizeQuery<S extends ListSchema>(schema: S, query: string, keys: readonly (keyof S & string)[]): string {
  return canonicalQuery(schema, parseParams(schema, new URLSearchParams(query)), keys);
}

/**
 * The patch that applies a view: every view key takes the view's value, and a
 * key the view does not mention returns to its default (so switching views
 * never leaves a stray filter behind).
 */
export function viewPatch<S extends ListSchema>(schema: S, query: string, keys: readonly (keyof S & string)[]): Partial<ListValues<S>> {
  const parsed = parseParams(schema, new URLSearchParams(query));
  const out: Record<string, unknown> = {};
  for (const key of keys) out[key] = (parsed as Record<string, unknown>)[key];
  return out as Partial<ListValues<S>>;
}

/** True when the value differs from the param's default (an active filter). */
export function isActiveValue(spec: ParamSpec, value: unknown): boolean {
  return serializeValue(spec, value) !== null;
}

/** How many of `keys` are active (the "סינון (n)" count). */
export function countActive<S extends ListSchema>(schema: S, values: ListValues<S>, keys: readonly (keyof S & string)[]): number {
  return keys.reduce((n, key) => (isActiveValue(schema[key], values[key]) ? n + 1 : n), 0);
}
